import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { type AppNotice, currentNotice, dismissNotice, subscribeNotice } from "@/lib/notice";
import { BottomToast, BOTTOM_TOAST_TAP } from "@/components/BottomToast";

/**
 * Renders whatever lib/notice.ts is currently saying (#3072). Mounted once in
 * the app shell. #5114: it renders through BottomToast, as UpdateToast (#769)
 * does, so the two share one box, anchor and lift and read as one system.
 * Both showing at once still sit on the same spot.
 *
 * Auto-clears after 8 seconds: the notices it carries report something that
 * did not save, which the user can act on by trying again, so it must not sit
 * on the screen demanding a dismissal.
 */
const AUTO_DISMISS_MS = 8000;

export function NoticeToast() {
  const [notice, setNotice] = useState<AppNotice | null>(() => currentNotice());

  useEffect(() => subscribeNotice(setNotice), []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(dismissNotice, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [notice]);

  if (!notice) return null;

  return (
    <BottomToast>
      {notice.tone === "success" ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      )}
      <span className="flex-1 leading-tight">{notice.text}</span>
      <button
        type="button"
        onClick={dismissNotice}
        className={`${BOTTOM_TOAST_TAP} shrink-0 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      >
        Dismiss
      </button>
    </BottomToast>
  );
}
